import { useMemo, useState } from 'react'
import { magasin } from '../lib/client/store.ts'
import { useMagasin } from '../lib/client/hooks.ts'
import { useRecettes } from './Recettes.tsx'
import { Sheet } from '../components/Sheet.tsx'
import { Coche } from '../components/Icone.tsx'

type Entree = {
  id: string
  date: string
  slot: 'midi' | 'soir'
  recipeId: string | null
  freeText: string | null
}

const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi']

/** AAAA-MM-JJ en heure LOCALE. `toISOString()` donnerait la veille après 22 h
 *  en été : une semaine de menus n'est pas un instant UTC. */
function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function Semaine() {
  const { etat } = useMagasin()
  const { recettes } = useRecettes()
  const [cible, setCible] = useState<{ date: string; slot: 'midi' | 'soir' } | null>(null)

  const jours = useMemo(() => {
    const base = magasin.maintenant()
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base)
      d.setDate(base.getDate() + i)
      return d
    })
  }, [])

  const entrees = useMemo(() => {
    const m = new Map<string, Entree>()
    for (const e of [...etat.mealPlanEntries.values()] as unknown as Entree[]) {
      if ((e as unknown as { deletedAt?: unknown }).deletedAt) continue
      m.set(`${e.date}|${e.slot}`, e)
    }
    return m
  }, [etat])

  const titre = (e: Entree | undefined) =>
    e ? (e.freeText ?? recettes.find((r) => r.recette.id === e.recipeId)?.recette.title ?? '—') : null

  return (
    <div className="pb-24">
      <div className="sticky top-0 z-20 bg-canvas/80 backdrop-blur-xl" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="flex h-[var(--size-bar)] items-center px-4">
          <h1 className="font-serif text-title text-ink">Semaine</h1>
        </div>
      </div>

      {/* Sept LIGNES, pas sept colonnes : la semaine est verticale sur un
          téléphone. Et on POSE au tap, pas au glisser — plus rapide et plus
          fiable au pouce. */}
      <div className="flex flex-col gap-3 px-4">
        {jours.map((d, i) => {
          const date = iso(d)
          return (
            <section key={date} className="rounded-md bg-surface p-3 shadow-row">
              <h2 className="mb-2 font-serif text-body text-ink">
                {i === 0 ? "Aujourd'hui" : i === 1 ? 'Demain' : JOURS[d.getDay()]}
                <span className="ml-2 tnum text-meta text-muted">{d.getDate()}</span>
              </h2>
              <div className="flex gap-2">
                {(['midi', 'soir'] as const).map((slot) => {
                  const e = entrees.get(`${date}|${slot}`)
                  return (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => setCible({ date, slot })}
                      className="pressable min-h-[56px] flex-1 rounded-sm bg-surface-sunk p-2 text-left"
                    >
                      <span className="block text-label uppercase text-muted">{slot}</span>
                      <span className="mt-0.5 block text-meta text-ink">{titre(e) ?? '—'}</span>
                    </button>
                  )
                })}
              </div>
            </section>
          )
        })}
      </div>

      <Sheet ouverte={!!cible} onFermer={() => setCible(null)} titre={cible ? `${cible.slot} du ${cible.date}` : ''}>
        {cible && (
          <>
            <button
              type="button"
              onClick={() => {
                magasin.dispatch('plan.set', { id: magasin.id(), ...cible, recipeId: null, freeText: null })
                setCible(null)
              }}
              className="pressable mb-3 h-11 w-full rounded-sm bg-surface-sunk text-body text-muted"
            >
              Vider ce créneau
            </button>
            <ul>
              {recettes.map(({ recette, manquants }) => (
                <li key={recette.id}>
                  <button
                    type="button"
                    onClick={() => {
                      magasin.dispatch('plan.set', { id: magasin.id(), ...cible, recipeId: recette.id })
                      setCible(null)
                    }}
                    className="pressable flex h-[52px] w-full items-center justify-between gap-3 rounded-sm px-2 text-left"
                  >
                    <span className="min-w-0 flex-1 truncate text-body text-ink">{recette.title}</span>
                    <span className="shrink-0 text-meta text-muted">
                      {manquants.length === 0 ? (
                        <Coche className="size-4 text-ok" />
                      ) : (
                        `−${manquants.length}`
                      )}
                    </span>
                  </button>
                </li>
              ))}
              {recettes.length === 0 && (
                <li className="py-4 text-center text-body text-muted">Aucune recette enregistrée.</li>
              )}
            </ul>
          </>
        )}
      </Sheet>
    </div>
  )
}
